import React, { useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

// ─── TAB MODES ───────────────────────────────────────────────────────────────
const MODES = {
  PLAY: "play",
  DIRECT: "direct",
};

// ─── DEFAULT VALUES ───────────────────────────────────────────────────────────
const DEFAULTS = {
  component: "com.trackliohub.dpc/com.trackliohub.dpc.MyDeviceAdminReceiver",
  checksum: "cj_EEO7-hsV6JGBVTQIV7X4N0R6AuZ36MaFL1U--CCU",
  apkUrl:
    "https://trackliohub.com/api/uploads/apk/APK-7-1782400998980-LWTT2D4URNM.apk",
};

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f5f5f5",
    padding: "32px 16px",
    fontFamily: "Arial, sans-serif",
  },
  container: {
    maxWidth: 900,
    margin: "0 auto",
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: "#666",
    marginTop: 4,
  },
  tabs: {
    display: "flex",
    gap: 0,
    border: "1px solid #ddd",
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 20,
    width: "fit-content",
  },
  tab: (active) => ({
    padding: "10px 24px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    background: active ? "#1976d2" : "#fff",
    color: active ? "#fff" : "#555",
    borderRight: "1px solid #ddd",
    transition: "all 0.15s",
  }),
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
  },
  card: {
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 12,
    padding: "20px 24px",
  },
  badge: (type) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 20,
    marginBottom: 12,
    background: type === "play" ? "#e8f5e9" : "#e3f2fd",
    color: type === "play" ? "#2e7d32" : "#1565c0",
  }),
  warn: {
    background: "#fff8e1",
    border: "1px solid #ffe082",
    color: "#f57f17",
    fontSize: 12,
    padding: "8px 12px",
    borderRadius: 8,
    marginBottom: 16,
  },
  fieldGroup: {
    marginBottom: 14,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    color: "#555",
    marginBottom: 5,
  },
  input: {
    width: "100%",
    padding: "8px 10px",
    fontSize: 13,
    border: "1px solid #ddd",
    borderRadius: 6,
    background: "#fafafa",
    color: "#1a1a1a",
    boxSizing: "border-box",
    fontFamily: "Arial, sans-serif",
  },
  textarea: {
    width: "100%",
    padding: "8px 10px",
    fontSize: 12,
    border: "1px solid #ddd",
    borderRadius: 6,
    background: "#fafafa",
    color: "#1a1a1a",
    boxSizing: "border-box",
    fontFamily: "monospace",
    resize: "vertical",
    minHeight: 64,
  },
  select: {
    width: "100%",
    padding: "8px 10px",
    fontSize: 13,
    border: "1px solid #ddd",
    borderRadius: 6,
    background: "#fafafa",
    color: "#1a1a1a",
  },
  divider: {
    border: "none",
    borderTop: "1px solid #eee",
    margin: "16px 0",
  },
  btnPrimary: {
    width: "100%",
    padding: "10px 0",
    fontSize: 14,
    fontWeight: 600,
    background: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    marginTop: 4,
  },
  btnSecondary: {
    width: "100%",
    padding: "9px 0",
    fontSize: 13,
    fontWeight: 500,
    background: "#fff",
    color: "#333",
    border: "1px solid #ccc",
    borderRadius: 8,
    cursor: "pointer",
    marginTop: 8,
  },
  previewEmpty: {
    textAlign: "center",
    padding: "48px 0",
    color: "#aaa",
    fontSize: 14,
  },
  previewCard: {
    textAlign: "center",
  },
  modeBadge: (type) => ({
    display: "inline-block",
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 20,
    marginBottom: 12,
    background: type === "play" ? "#e8f5e9" : "#fff8e1",
    color: type === "play" ? "#2e7d32" : "#f57f17",
  }),
  qrWrap: {
    display: "flex",
    justifyContent: "center",
    margin: "12px 0",
  },
  jsonBox: {
    background: "#f9f9f9",
    border: "1px solid #eee",
    borderRadius: 8,
    padding: "10px 12px",
    textAlign: "left",
    fontFamily: "monospace",
    fontSize: 11,
    color: "#444",
    wordBreak: "break-all",
    maxHeight: 180,
    overflowY: "auto",
    marginTop: 12,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#888",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
};

// ─── FIELD COMPONENT ─────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={S.fieldGroup}>
    <label style={S.label}>{label}</label>
    {children}
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const DeviceOwnerQrGenerator = () => {
  const [mode, setMode] = useState(MODES.PLAY);
  const [showJson, setShowJson] = useState(false);
  const qrRef = useRef(null);

  // Play Store mode fields
  const [playForm, setPlayForm] = useState({
    component: DEFAULTS.component,
    checksum: DEFAULTS.checksum,
    wifiSsid: "",
    wifiPassword: "",
    wifiSecurity: "WPA",
  });

  // Direct APK mode fields
  const [directForm, setDirectForm] = useState({
    component: DEFAULTS.component,
    apkUrl: DEFAULTS.apkUrl,
    checksum: DEFAULTS.checksum,
    wifiSsid: "",
    wifiPassword: "",
    wifiSecurity: "WPA",
  });

  const [qrValue, setQrValue] = useState("");
  const [activeMode, setActiveMode] = useState(null);

  // ── payload builders ──────────────────────────────────────────────────────
 const buildPlayPayload = () => {
  const payload = {
    "android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME":
      playForm.component,

    "android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_NAME":
      "com.trackliohub.dpc",

    "android.app.extra.PROVISIONING_DEVICE_ADMIN_SIGNATURE_CHECKSUM":
      playForm.checksum,

    "android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED": true,

    "android.app.extra.PROVISIONING_SKIP_ENCRYPTION": true,
  };

  if (playForm.wifiSsid) {
    payload["android.app.extra.PROVISIONING_WIFI_SSID"] =
      playForm.wifiSsid;

    payload["android.app.extra.PROVISIONING_WIFI_PASSWORD"] =
      playForm.wifiPassword;

    payload["android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE"] =
      playForm.wifiSecurity;
  }

  return payload;
};

  const buildDirectPayload = () => {
    const payload = {
      "android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME":
        directForm.component,
      "android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION":
        directForm.apkUrl,
      "android.app.extra.PROVISIONING_DEVICE_ADMIN_SIGNATURE_CHECKSUM":
        directForm.checksum,
      "android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED": true,
      "android.app.extra.PROVISIONING_SKIP_ENCRYPTION": true,
    };
    if (directForm.wifiSsid) {
      payload["android.app.extra.PROVISIONING_WIFI_SSID"] =
        directForm.wifiSsid;
      payload["android.app.extra.PROVISIONING_WIFI_PASSWORD"] =
        directForm.wifiPassword;
      payload["android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE"] =
        directForm.wifiSecurity;
    }
    return payload;
  };

  // ── generate ──────────────────────────────────────────────────────────────
  const handleGenerate = () => {
    const payload =
      mode === MODES.PLAY ? buildPlayPayload() : buildDirectPayload();
    setQrValue(JSON.stringify(payload));
    setActiveMode(mode);
    setShowJson(false);
  };

  // ── download ──────────────────────────────────────────────────────────────
  const handleDownload = () => {
    const canvas = document.querySelector("#qr-output canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download =
      activeMode === MODES.PLAY
        ? "tracklio-playstore-qr.png"
        : "tracklio-direct-qr.png";
    a.click();
  };

  // ── form helpers ──────────────────────────────────────────────────────────
  const updatePlay = (e) =>
    setPlayForm({ ...playForm, [e.target.name]: e.target.value });
  const updateDirect = (e) =>
    setDirectForm({ ...directForm, [e.target.name]: e.target.value });

  const currentPayloadJson =
    qrValue ? JSON.stringify(JSON.parse(qrValue), null, 2) : "";

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.container}>
        {/* Header */}
        <div style={S.header}>
          <h1 style={S.title}>Tracklio DPC — QR Generator</h1>
          <p style={S.subtitle}>
            Android Enterprise Device Owner provisioning QR
          </p>
        </div>

        {/* Tabs */}
        <div style={S.tabs}>
          <button
            style={S.tab(mode === MODES.PLAY)}
            onClick={() => setMode(MODES.PLAY)}
          >
            ✅ Play Store mode
          </button>
          <button
            style={{ ...S.tab(mode === MODES.DIRECT), borderRight: "none" }}
            onClick={() => setMode(MODES.DIRECT)}
          >
            ⚡ Direct APK mode
          </button>
        </div>

        {/* Grid */}
        <div style={S.grid}>
          {/* ── LEFT: Form ── */}
          <div style={S.card}>
            {mode === MODES.PLAY ? (
              <>
                <div style={S.badge("play")}>
                  ✓ Recommended — Play Protect safe
                </div>
                <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
                  APK automatically downloads from Play Store. No server URL
                  needed.
                </p>

                <Field label="Device admin component">
                  <input
                    style={S.input}
                    name="component"
                    value={playForm.component}
                    onChange={updatePlay}
                  />
                </Field>

                <Field label="Signature checksum (Play Console → App Signing)">
                  <input
                    style={S.input}
                    name="checksum"
                    value={playForm.checksum}
                    onChange={updatePlay}
                  />
                </Field>

                <hr style={S.divider} />
                <p style={S.sectionTitle}>WiFi (optional)</p>

                <Field label="SSID">
                  <input
                    style={S.input}
                    name="wifiSsid"
                    placeholder="MyWifi"
                    value={playForm.wifiSsid}
                    onChange={updatePlay}
                  />
                </Field>

                <Field label="Password">
                  <input
                    style={S.input}
                    name="wifiPassword"
                    type="password"
                    placeholder="••••••••"
                    value={playForm.wifiPassword}
                    onChange={updatePlay}
                  />
                </Field>

                <Field label="Security type">
                  <select
                    style={S.select}
                    name="wifiSecurity"
                    value={playForm.wifiSecurity}
                    onChange={updatePlay}
                  >
                    <option value="WPA">WPA/WPA2</option>
                    <option value="WEP">WEP</option>
                    <option value="nopass">None</option>
                  </select>
                </Field>
              </>
            ) : (
              <>
                <div style={S.badge("direct")}>⚡ Direct APK download</div>
                <div style={S.warn}>
                  ⚠️ Server-hosted APK se Play Protect block aa sakta hai.
                  Play Console re-signed APK use karo.
                </div>

                <Field label="Device admin component">
                  <input
                    style={S.input}
                    name="component"
                    value={directForm.component}
                    onChange={updateDirect}
                  />
                </Field>

                <Field label="APK download URL">
                  <textarea
                    style={S.textarea}
                    name="apkUrl"
                    value={directForm.apkUrl}
                    onChange={updateDirect}
                  />
                </Field>

                <Field label="Signature checksum">
                  <input
                    style={S.input}
                    name="checksum"
                    value={directForm.checksum}
                    onChange={updateDirect}
                  />
                </Field>

                <hr style={S.divider} />
                <p style={S.sectionTitle}>WiFi (optional)</p>

                <Field label="SSID">
                  <input
                    style={S.input}
                    name="wifiSsid"
                    placeholder="MyWifi"
                    value={directForm.wifiSsid}
                    onChange={updateDirect}
                  />
                </Field>

                <Field label="Password">
                  <input
                    style={S.input}
                    name="wifiPassword"
                    type="password"
                    placeholder="••••••••"
                    value={directForm.wifiPassword}
                    onChange={updateDirect}
                  />
                </Field>

                <Field label="Security type">
                  <select
                    style={S.select}
                    name="wifiSecurity"
                    value={directForm.wifiSecurity}
                    onChange={updateDirect}
                  >
                    <option value="WPA">WPA/WPA2</option>
                    <option value="WEP">WEP</option>
                    <option value="nopass">None</option>
                  </select>
                </Field>
              </>
            )}

            <button style={S.btnPrimary} onClick={handleGenerate}>
              Generate QR Code
            </button>
          </div>

          {/* ── RIGHT: Preview ── */}
          <div style={S.card}>
            {!qrValue ? (
              <div style={S.previewEmpty}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>⬜</div>
                <p>QR yahan generate hoga</p>
              </div>
            ) : (
              <div style={S.previewCard}>
                <div style={S.modeBadge(activeMode)}>
                  {activeMode === MODES.PLAY
                    ? "✅ Play Store mode"
                    : "⚡ Direct APK mode"}
                </div>

                <div id="qr-output" style={S.qrWrap} ref={qrRef}>
                  <QRCodeCanvas
                    value={qrValue}
                    size={240}
                    level="M"
                    includeMargin={true}
                  />
                </div>

                <button style={S.btnPrimary} onClick={handleDownload}>
                  ⬇ Download PNG
                </button>

                <button
                  style={S.btnSecondary}
                  onClick={() => setShowJson((v) => !v)}
                >
                  {showJson ? "Hide" : "Show"} JSON payload
                </button>

                {showJson && (
                  <pre style={S.jsonBox}>{currentPayloadJson}</pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceOwnerQrGenerator;